import React, { useCallback, useState } from "react"

import { useCovalOnly } from "@/feature-flags"
import {
  toSimulationOutputIds,
  useResimulateSimulationOutputsTransport,
} from "@/runs"
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from "@coval/ui"
import { RefreshCw } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { LocalWorkerRoutingWarning } from "@/components/LocalWorkerRoutingWarning"
import { useDevId } from "@/hooks/useDevId"

interface ResimulateButtonProps {
  simulationOutputIds: string[]
  /**
   * Suppresses the local-worker option, leaving a plain production resimulate.
   * Set on read-only surfaces such as a public shared-run link, where handing
   * the run to an engineer's machine is not what the viewer is there to do.
   */
  isShared?: boolean
}

export const ResimulateButton: React.FC<ResimulateButtonProps> = ({
  simulationOutputIds,
  isShared = false,
}) => {
  const showsDialog = useCovalOnly() && !isShared
  const [isOpen, setIsOpen] = useState(false)
  const devId = useDevId()
  const routesToLocalWorker = Boolean(devId) && !isShared
  const { trackEvent } = useAnalytics()

  const resimulate = useResimulateSimulationOutputsTransport()

  const handleResimulate = useCallback(async () => {
    const validIds = simulationOutputIds.filter(
      (id) => typeof id === "string" && id.trim().length > 0,
    )

    if (validIds.length === 0) {
      return
    }

    setIsOpen(false)

    try {
      const result = await resimulate.mutateAsync({
        simulation_output_ids: toSimulationOutputIds(validIds),
        ...(routesToLocalWorker ? { dev_id: devId } : {}),
      })

      const queued = result.queued ?? []
      const failed = result.failed ?? []

      // Recursive-improvement loop: rerunning individual outputs in place.
      trackEvent(TRACKING_EVENTS.RUNS.ROWS_RESIMULATED, {
        output_count: validIds.length,
        queued_count: queued.length,
        failed_count: failed.length,
        dev_routed: routesToLocalWorker,
      })

      const parts: string[] = [
        `${queued.length} simulation${queued.length !== 1 ? "s" : ""} queued for resimulation`,
      ]
      if (failed.length > 0) {
        parts.push(
          `${failed.length} failed: ${failed.map((f) => f.error).join(", ")}`,
        )
      }

      if (failed.length > 0) {
        toast.danger({
          title: "Partially queued",
          description: parts.join(". "),
        })
      } else {
        toast.success({
          title: "Queued",
          description: parts.join(". "),
        })
      }
    } catch (err) {
      toast.error({
        title: "Resimulation failed",
        error: err,
        fallback: "An unexpected error occurred.",
      })
    }
  }, [simulationOutputIds, resimulate, devId, routesToLocalWorker, trackEvent])

  const handlePress = useCallback(() => {
    if (showsDialog) {
      setIsOpen(true)
    } else {
      void handleResimulate()
    }
  }, [showsDialog, handleResimulate])

  const count = simulationOutputIds.length

  return (
    <>
      <Button
        variant="secondary"
        onClick={handlePress}
        disabled={count === 0 || resimulate.isPending}
        title="Rerun selected simulated conversations in place with latest agent/metric config"
      >
        <RefreshCw className="h-4 w-4" />
        {resimulate.isPending ? "Resimulating…" : `Resimulate (${count})`}
      </Button>

      {showsDialog && (
        <Dialog
          open={isOpen}
          onOpenChange={(open) => !open && setIsOpen(false)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Resimulate {count} simulation{count !== 1 ? "s" : ""}
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <div className="flex flex-col gap-4">
                <LocalWorkerRoutingWarning />
                <div className="rounded-md bg-surface-1 p-3 text-xs text-coval-text-muted">
                  <p className="mb-1 font-medium text-coval-text">
                    What this does
                  </p>
                  <p>
                    Reruns each selected simulation in place using the{" "}
                    <span className="font-medium">latest</span> agent, persona,
                    test set, test case, and metric configuration. Existing
                    simulation output and metric values are overwritten — the
                    original run records are kept but their results are
                    replaced.
                  </p>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => void handleResimulate()}
                  disabled={resimulate.isPending}
                >
                  {resimulate.isPending
                    ? "Resimulating…"
                    : `Resimulate ${count} simulation${count !== 1 ? "s" : ""}`}
                </Button>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
